import * as context from 'api/base';
import { getChannelSettingByKey } from 'app/layouts/whitelabel/routes/generic/Helpers/GenericIndexHelper';
import { getNewBuyInsurancePathGeneric } from 'app/layouts/whitelabel/routes/generic/Helpers/HomePageHelper';
import store from 'app/store';

// Both logo columns are optional per channel, so their widths cannot be baked into the menu widths.
// The two are equal because each holds the same thing - a centred logo over a wrapped compliance
// caption - and they are meant to read as a matched pair at either end of the row. 3 apiece is also
// what leaves the three menu columns 2 units each, which is roughly the width the contact column's
// text actually fills; any wider and the unused space pools against the tertiary logo, which is what
// made the gap on that side look twice the one on the other.
export const SECONDARY_LOGO_MD = 3;
export const TERTIARY_LOGO_MD = 3;

// A single link list wider than a third of the row reads as a stray block rather than a column.
const MAX_MENU_MD = 4;

// Each menu's `sm` (the 600-899 tier) is 4, a third of the row, not 6. At a half the menus pair off
// with the logo columns, so a centred logo block ends up beside a left-aligned link list and the row
// stops reading as a grid. A third puts all three menus on one row and leaves each logo a full-width
// row of its own, which is the same logo / menus / logo rhythm the desktop layout has.

// Deriving the menu widths from whichever logo columns are actually present is what keeps the row
// at 12 instead of leaving a channel's footer as a narrow island on a wide screen - a channel with a
// secondary logo but no tertiary logo used to reach only 9. footerConfig always builds three menus,
// and with equal logo columns the remainder is 6, 9 or 12, all divisible by 3, so the real case is
// always a row of 12 with equal menu columns. Rows of one or two menus (every link in a column
// hidden) still fall short once MAX_MENU_MD caps them; the caller centres those.
export const getFooterMenuWidths = ({ hasSecondaryLogo, hasTertiaryLogo, menuCount }) => {
	if (menuCount < 1) return [];

	const remaining = 12 - (hasSecondaryLogo ? SECONDARY_LOGO_MD : 0) - (hasTertiaryLogo ? TERTIARY_LOGO_MD : 0);
	const base = Math.floor(remaining / menuCount);
	const leftover = remaining - base * menuCount;

	// Leftover columns go to the right-most menus, which carry the longest strings. This is a
	// tie-breaker no reachable configuration currently exercises - the three-menu remainder always
	// divides evenly, and the one two-menu case with a remainder is capped back to equal widths by
	// MAX_MENU_MD - so it only matters if the menu count ever stops being three.
	return Array.from({ length: menuCount }, (_, index) =>
		Math.min(base + (index >= menuCount - leftover ? 1 : 0), MAX_MENU_MD)
	);
};

export const footerConfig = ({ companyDetailsResponse }) => {
	const infoSettings = context.getSettings();
	const channelProducts = store.getState()?.fuse.form.channelProducts;
	const referenceTokenDetails = store.getState()?.fuse.form.referenceTokenDetails;
	const { address, address2, address3, city, province, country, postalCode, contactNumber, contactEMail } =
		companyDetailsResponse?.value?.companyContactViewModel || {};
	const myHostPrefix = store.getState()?.fuse.form.mySite.hostPrefix;

	// Get address from channel settings
	const addressLine1 = getChannelSettingByKey('Address-Line1') || '';
	const addressLine2 = getChannelSettingByKey('Address-Line2') || '';
	const addressLine3 = getChannelSettingByKey('Address-Line3') || '';
	const addressEmail = getChannelSettingByKey('Email-Address') || '';
	const addressPhone = getChannelSettingByKey('Phone-Number') || '';
	const addressProvince = getChannelSettingByKey('Address-Province') || '';
	const addressCity = getChannelSettingByKey('Address-City') || '';
	const addressCountry = getChannelSettingByKey('Address-Country') || '';
	const addressPostalCode = getChannelSettingByKey('Address-PostalCode') || '';

	const channelAddress = [
		addressLine1,
		addressLine2,
		addressLine3,
		addressCity,
		addressProvince,
		addressCountry,
		addressPostalCode
	]
		.filter(part => part)
		.join(' ');

	return {
		menus: [
			{
				id: 1,
				title: infoSettings.textFooterHeader1,
				menuItems: [
					{
						title: infoSettings.footerFaqText,
						url: '/faq',
						isVisible: infoSettings?.footerMenuFaqVisible === 'true'
					},
					{
						title: infoSettings.footerTermsAndCondText,
						url: '/terms-and-conditions',
						isvisible: infoSettings?.footerMenuTermsAndConditionsVisible === 'true'
					},
					{
						title: infoSettings.footerPrivacyPolicyText,
						url: '/privacy-policy',
						isVisible: infoSettings?.footerMenuPrivacyPolicyVisible === 'true'
					},
					{
						title: 'Disclosure',
						url: '/disclosure',
						isVisible: infoSettings?.footerMenuDisclosureVisible === 'true'
					},
					{
						title: infoSettings.footerDetailsText,
						url: '/details',
						isVisible: infoSettings?.footerMenuDetailsVisible === 'true'
					}
				],
				xs: 12,
				sm: 4
			},
			{
				id: 2,
				title: infoSettings.textFooterHeader2,
				menuItems: [
					{
						title: infoSettings.footerContactText,
						url: '/contact',
						isVisible: infoSettings.footerMenuContactVisible === 'true'
					},
					{
						title: infoSettings.footerAboutText,
						url: '/about',
						isVisible: infoSettings.footerMenuAboutVisible === 'true'
					},
					{
						title: 'Buy Insurance',
						url: getNewBuyInsurancePathGeneric(channelProducts, referenceTokenDetails?.token, myHostPrefix),
						isVisible: infoSettings.footerMenuInsuranceVisible === 'true'
					},
					{
						title: 'Submit Claim',
						url: '/submit-claim',
						isVisible: infoSettings.footerMenuClaimVisible === 'true'
					}
				],
				xs: 12,
				sm: 4
			},
			{
				id: 3,
				title: infoSettings.textAddressHeader,
				menuItems: [
					channelAddress?.length > 0
						? {
								title: channelAddress,
								url: '#',
								isVisible: true
							}
						: {
								title: `${address || ''} ${address2 || ''} ${address3 || ''} ${city || ''} ${province || ''} ${
									country || ''
								} ${postalCode || ''}`,
								url: '#',
								isVisible: true
							},
					{
						title: addressPhone ? addressPhone : `${contactNumber || ''}`,
						url: contactNumber,
						isVisible: true
					},
					{
						title: addressEmail ? addressEmail : `${contactEMail || ''}`,
						url: `mailTo:${contactEMail}`,
						isVisible: true
					}
				],
				xs: 12,
				sm: 4
			}
		]
	};
};
